import { useCallback, useEffect, useState } from 'react';
import StorageService from '../services/StorageService';

const STORAGE_KEY = 'voipLastRecentsViewedAt';

// Module-level subscriber set so every hook instance in the same tab stays
// in sync. The browser `storage` event only fires for OTHER tabs, so without
// this an in-tab `markViewedNow()` would only update the calling component's
// state and leave NavigationWrapper's badge counting stale data.
const listeners = new Set();
function notifyInTab(value) {
  listeners.forEach((cb) => cb(value));
}

function readStored() {
  const raw = StorageService.readEntry(STORAGE_KEY);
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Tracks the last time the user opened the VoIP Recents tab. Used to decide
 * which inbound unanswered calls count as "new" for the notification badge.
 * Persisted in localStorage per browser (see StorageService).
 */
export default function useLastRecentsViewedAt() {
  const [lastViewedAtMs, setLastViewedAtMs] = useState(readStored);

  // In-tab fan-out: each instance subscribes its setter so a markViewedNow()
  // call from any component re-renders every consumer.
  useEffect(() => {
    listeners.add(setLastViewedAtMs);
    return () => listeners.delete(setLastViewedAtMs);
  }, []);

  // Cross-tab sync via the storage event.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        const n = Number(e.newValue);
        setLastViewedAtMs(Number.isFinite(n) && n > 0 ? n : 0);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const markViewedNow = useCallback(() => {
    const now = Date.now();
    StorageService.createEntry(STORAGE_KEY, String(now));
    notifyInTab(now);
  }, []);

  return { lastViewedAtMs, markViewedNow };
}
